import { useEffect, useRef, useState } from "react";

// One shared AudioContext for every tile's level meter (dozens of contexts
// would be wasteful and hit browser limits).
let sharedCtx: AudioContext | null = null;
function getAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const AC =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (!AC) return null;
  if (!sharedCtx) sharedCtx = new AC();
  if (sharedCtx.state === "suspended") void sharedCtx.resume();
  return sharedCtx;
}

/**
 * Whether there's audible speech on a stream's audio track right now. Powers
 * the mic activity animation so a user can tell "my mic is capturing" (my own
 * tile animates) from "their audio is arriving" (their tile animates → if I
 * still hear nothing, it's MY speaker, not their mic). `enabled` turns it off
 * when the mic is muted so a muted tile never animates.
 */
export function useSpeaking(
  stream: MediaStream | null,
  enabled: boolean,
): boolean {
  const [speaking, setSpeaking] = useState(false);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!enabled || !stream) {
      setSpeaking(false);
      return;
    }
    const track = stream.getAudioTracks()[0];
    const ctx = getAudioContext();
    if (!track || !ctx) {
      setSpeaking(false);
      return;
    }
    let source: MediaStreamAudioSourceNode;
    let analyser: AnalyserNode;
    try {
      // Wrap in a fresh single-track stream so the source only sees audio.
      source = ctx.createMediaStreamSource(new MediaStream([track]));
      analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.5;
      source.connect(analyser);
    } catch {
      setSpeaking(false);
      return;
    }
    const data = new Uint8Array(analyser.frequencyBinCount);
    let active = false;
    let quietFrames = 0;
    const tick = () => {
      analyser.getByteFrequencyData(data);
      let sum = 0;
      for (let i = 0; i < data.length; i += 1) sum += data[i];
      const avg = sum / data.length;
      if (avg > 15) {
        quietFrames = 0;
        if (!active) {
          active = true;
          setSpeaking(true);
        }
      } else {
        quietFrames += 1;
        // Small hangover so brief pauses between words don't flicker the ring.
        if (active && quietFrames > 12) {
          active = false;
          setSpeaking(false);
        }
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafRef.current);
      try {
        source.disconnect();
        analyser.disconnect();
      } catch {
        /* ignore */
      }
    };
  }, [stream, enabled]);

  return speaking;
}
